import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Concern, CreateConcernDTO, UpdateConcernDTO } from '@promo/shared';
import { get, post, put, deleteFn } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

/**
 * React-Query powered hook for opportunities (concerns)
 */
export function useConcerns(packetId?: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const cacheKey = ['concerns', packetId];

  // Fetch list
  const concernsQuery = useQuery<Concern[], Error>({
    queryKey: cacheKey,
    queryFn: () => {
      if (!packetId) return Promise.resolve([]);
      return get<Concern[]>('/concerns', { packetId });
    },
    enabled: !!packetId,
  });

  // Create
  const createMutation = useMutation<Concern, Error, CreateConcernDTO>({
    mutationFn: (data) => post<Concern>('/concerns', data),
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to add concern: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  const createConcern = (
    data: CreateConcernDTO,
    opts?: { onSuccess?: () => void },
  ) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: cacheKey });
        toast({ title: 'Saved', description: 'Concern added successfully' });
        opts?.onSuccess?.();
      },
    });
  };

  // Update
  const updateMutation = useMutation<Concern, Error, { id: string; data: UpdateConcernDTO }>({
    mutationFn: ({ id, data }) => put<Concern>(`/concerns/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cacheKey });
      toast({ title: 'Saved', description: 'Concern updated' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: `Failed to update concern: ${error.message}`, variant: 'destructive' });
    },
  });

  // Delete
  const deleteMutation = useMutation<void, Error, string>({
    mutationFn: (id) => deleteFn<void>(`/concerns/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cacheKey });
      toast({ title: 'Deleted', description: 'Concern removed' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: `Failed to delete concern: ${error.message}`, variant: 'destructive' });
    },
  });

  return {
    concerns: concernsQuery.data || [],
    isLoadingConcerns: concernsQuery.isLoading,
    concernsError: concernsQuery.error,

    createConcern,
    isCreatingConcern: createMutation.isPending,

    updateConcern: (id: string, data: UpdateConcernDTO) => updateMutation.mutate({ id, data }),
    isUpdatingConcern: updateMutation.isPending,

    deleteConcern: (id: string) => deleteMutation.mutate(id),
    isDeletingConcern: deleteMutation.isPending,

    refetchConcerns: concernsQuery.refetch,
  };
} 